/**
 * Default model to select when no localStorage state is found
 * @private
 * @constant {string}
 */
const _DEFAULT_MODEL_NAME = 'og_plus'

/**
 * Available font-family options
 * @private
 * @constant {Array<{value: string, label: string}>}
 */
const _FONT_FAMILIES = [
  { value: 'trmnl', label: 'TRMNL Fonts' },
  { value: 'classic', label: 'Classic Fonts' }
]

/**
 * Default font-family to select when no localStorage state is found
 * @private
 * @constant {string}
 */
const _DEFAULT_FONT_FAMILY = 'trmnl'

/**
 * localStorage key for caching API responses (models and palettes)
 * @private
 * @constant {string}
 */
const _API_CACHE_KEY = 'trmnl-picker-api-cache'

/**
 * Cache TTL in milliseconds (1 day)
 * @private
 * @constant {number}
 */
const _CACHE_TTL_MS = 24 * 60 * 60 * 1000

/**
 * Event fired when picker state changes
 * @event TRMNLPicker#trmnl:change
 * @type {CustomEvent}
 * @property {Object} detail - Event details
 * @property {string} detail.origin - What triggered the change: 'constructor', 'form', or 'setParams'
 * @property {Array<string>} detail.screenClasses - Array of CSS classes for Framework CSS rendering
 * @property {Object} detail.model - Current model object with name, label, width, height, kind, css properties
 * @property {Object} detail.palette - Current palette object with id, name, framework_class properties
 * @property {boolean} detail.isPortrait - Portrait orientation flag
 * @property {boolean} detail.isDarkMode - Dark mode flag
 * @property {string} detail.fontFamily - Font family ('trmnl' or 'classic')
 * @property {number} detail.width - Current width of the screen in pixels
 * @property {number} detail.height - Current height of the screen in pixels
 *
 * @example
 * picker.formElement.addEventListener('trmnl:change', (event) => {
 *   const { origin, screenClasses, model, palette } = event.detail
 *   console.log(`Changed via ${origin}`)
 *   console.log('Classes:', screenClasses)
 * })
 */

/**
 * TRMNLPicker - Vanilla JS library for TRMNL device and palette selection
 *
 * Provides a reactive picker component that manages device models, color palettes,
 * orientation, and display mode. Emits 'trmnl:change' events with current state
 * and CSS classes for rendering.
 * 
 * **[View a live demo →](https://usetrmnl.github.io/trmnl-picker/example/)**
 *
 * **Note:** Using the constructor directly is not recommended. Use the static
 * {@link TRMNLPicker.create} method instead, which automatically fetches
 * models and palettes from the TRMNL API if not provided.
 *
 * @class TRMNLPicker
 * @param {string|Element} formIdOrElement - Form element ID or DOM element reference
 * @param {Object} options - Configuration options
 * @param {Array<Object>} options.models - Array of model objects from TRMNL API
 * @param {Array<Object>} options.palettes - Array of palette objects from TRMNL API
 * @param {string} [options.localStorageKey] - Optional key for persisting state to localStorage
 *
 * @fires TRMNLPicker#trmnl:change
 *
 * @example
 * // HTML Structure - Required form with data-* attributes
 * // <form id="picker-form">
 * //   <!-- Required: Model selector -->
 * //   <select data-model-select></select>
 * //
 * //   <!-- Required: Palette selector -->
 * //   <select data-palette-select></select>
 * //
 * //   <!-- Optional: Orientation toggle -->
 * //   <button type="button" data-orientation-toggle>
 * //     <span data-orientation-text>Landscape</span>
 * //   </button>
 * //
 * //   <!-- Optional: Dark mode toggle -->
 * //   <button type="button" data-dark-mode-toggle>
 * //     <span data-dark-mode-text>Light Mode</span>
 * //   </button>
 * //
 * //   <!-- Optional: Font-family selector -->
 * //   <select data-font-family-select></select>
 * //
 * //   <!-- Optional: Reset button -->
 * //   <button type="button" data-reset-button>Reset</button>
 * // </form>
 *
 * // Create with element ID
 * const picker = new TRMNLPicker('picker-form', { models, palettes })
 *
 * // Create with DOM element
 * const element = document.getElementById('picker-form')
 * const picker = new TRMNLPicker(element, { models, palettes })
 *
 * // Listen for changes
 * picker.formElement.addEventListener('trmnl:change', (event) => {
 *   console.log(event.detail.screenClasses)
 * })
 */
class TRMNLPicker {
  static API_BASE_URL = 'https://trmnl.com'

  /**
   * Get cached API response from localStorage
   * @private
   * @static
   * @returns {{models: Array, palettes: Array} | null} Cached data or null if expired/missing
   */
  static _getCachedApiData() {
    try {
      const cached = localStorage.getItem(_API_CACHE_KEY)
      if (!cached) return null

      const { timestamp, models, palettes } = JSON.parse(cached)
      const now = Date.now()

      // Check if cache is still valid
      if (now - timestamp > _CACHE_TTL_MS) {
        localStorage.removeItem(_API_CACHE_KEY)
        return null
      }

      return { models, palettes }
    } catch (error) {
      console.warn('TRMNLPicker: Failed to read API cache:', error)
      return null
    }
  }

  /**
   * Save API response to localStorage cache
   * @private
   * @static
   * @param {Array} models - Models array
   * @param {Array} palettes - Palettes array
   */
  static _setCachedApiData(models, palettes) {
    try {
      const cacheData = {
        timestamp: Date.now(),
        models,
        palettes
      }
      localStorage.setItem(_API_CACHE_KEY, JSON.stringify(cacheData))
    } catch (error) {
      console.warn('TRMNLPicker: Failed to save API cache:', error)
    }
  }

  /**
   * Create a TRMNLPicker instance, fetching data from TRMNL API if not provided
   *
   * Automatically caches API responses in localStorage for 24 hours to reduce network requests.
   *
   * @static
   * @param {string|Element} formIdOrElement - Form element ID or DOM element
   * @param {Object} options - Configuration options
   * @param {Array<Object>} [options.models] - Optional models array (fetched from API if not provided)
   * @param {Array<Object>} [options.palettes] - Optional palettes array (fetched from API if not provided)
   * @param {string} [options.localStorageKey] - Optional key for state persistence
   * @returns {Promise<TRMNLPicker>} Promise resolving to picker instance
   * @throws {Error} If API fetch fails when models or palettes are not provided
   *
   * @example
   * // Fetch models and palettes from API (or use cached data if available)
   * const picker = await TRMNLPicker.create('screen-picker')
   *
   * // Provide your own data
   * const picker = await TRMNLPicker.create('screen-picker', { models, palettes })
   */
  static async create(formId, options = {}) {
    let { models, palettes, localStorageKey } = options

    // Try to use cached data if neither models nor palettes are provided
    if (!models && !palettes) {
      const cached = TRMNLPicker._getCachedApiData()
      if (cached) {
        models = cached.models
        palettes = cached.palettes
      }
    }

    // Fetch models if not provided and not in cache
    if (!models) {
      try {
        const response = await fetch(`${TRMNLPicker.API_BASE_URL}/api/models`)
        if (!response.ok) {
          throw new Error(`Failed to fetch models: ${response.status} ${response.statusText}`)
        }
        const data = await response.json()
        models = data.data || data
      } catch (error) {
        throw new Error(`TRMNLPicker: Failed to fetch models from API: ${error.message}`)
      }
    }

    // Fetch palettes if not provided and not in cache
    if (!palettes) {
      try {
        const response = await fetch(`${TRMNLPicker.API_BASE_URL}/api/palettes`)
        if (!response.ok) {
          throw new Error(`Failed to fetch palettes: ${response.status} ${response.statusText}`)
        }
        const data = await response.json()
        palettes = data.data || data
      } catch (error) {
        throw new Error(`TRMNLPicker: Failed to fetch palettes from API: ${error.message}`)
      }
    }

    // Cache the API data for future use (if both were fetched)
    if (!options.models && !options.palettes) {
      TRMNLPicker._setCachedApiData(models, palettes)
    }

    return new TRMNLPicker(formId, { models, palettes, localStorageKey })
  }

  constructor(formIdOrElement, options = {}) {
    // Validate inputs
    if (!formIdOrElement) {
      throw new Error('TRMNLPicker: formIdOrElement is required')
    }

    // Store references - accept either string ID or DOM element
    if (typeof formIdOrElement === 'string') {
      this.formElement = document.getElementById(formIdOrElement)
      if (!this.formElement) {
        throw new Error(`TRMNLPicker: Form element with id "${formIdOrElement}" not found`)
      }
    } else if (formIdOrElement instanceof Element) {
      this.formElement = formIdOrElement
    } else {
      throw new Error('TRMNLPicker: formIdOrElement must be a string ID or DOM element')
    }

    const { models, palettes, localStorageKey } = options

    this.models = models
    this.palettes = palettes
    this.localStorageKey = localStorageKey

    // Only initialize if we have data
    if (this.models && this.palettes) {
      if (!Array.isArray(this.models) || this.models.length === 0) {
        throw new Error('TRMNLPicker: models must be a non-empty array')
      }

      if (!Array.isArray(this.palettes) || this.palettes.length === 0) {
        throw new Error('TRMNLPicker: palettes must be a non-empty array')
      }

      // Filter out models where all palettes have empty framework_class
      this.models = this._filterValidModels()

      if (this.models.length === 0) {
        throw new Error('TRMNLPicker: no valid models found (all models have palettes with empty framework_class)')
      }

      // Initialize DOM elements and bind events
      this._initializeElements()
      this._bindEvents()

      // Set initial state (will load from localStorage if available)
      this._setInitialState()
    }
  }

  /**
   * Filter out models where all their palettes have empty framework_class
   * @private
   * @returns {Array<Object>} Filtered models array
   */
  _filterValidModels() {
    return this.models.filter(model => {
      // Check if at least one palette for this model has a non-empty framework_class
      return model.palette_ids.some(paletteId => {
        const palette = this.palettes.find(p => p.id === paletteId)
        return palette && palette.framework_class && palette.framework_class.trim() !== ''
      })
    })
  }

  /**
   * Get the first valid palette ID for a model (one with non-empty framework_class)
   * @private
   * @param {Object} model - Model object
   * @returns {string|null} First valid palette ID or null
   */
  _getFirstValidPaletteId(model) {
    if (!model) return null

    for (const paletteId of model.palette_ids) {
      const palette = this.palettes.find(p => p.id === paletteId)
      if (palette && palette.framework_class && palette.framework_class.trim() !== '') {
        return paletteId
      }
    }

    return null
  }

  /**
   * Find and store references to form elements using data-* attributes
   * @private
   */
  _initializeElements() {
    this.elements = {
      modelSelect: this.formElement.querySelector('[data-model-select]'),
      paletteSelect: this.formElement.querySelector('[data-palette-select]'),
      orientationToggle: this.formElement.querySelector('[data-orientation-toggle]'),
      darkModeToggle: this.formElement.querySelector('[data-dark-mode-toggle]'),
      fontFamilySelect: this.formElement.querySelector('[data-font-family-select]'),
      resetButton: this.formElement.querySelector('[data-reset-button]'),

      // Optional: UI indicator elements
      orientationText: this.formElement.querySelector('[data-orientation-text]'),
      darkModeText: this.formElement.querySelector('[data-dark-mode-text]')
    }

    // Validate required elements
    const required = ['modelSelect', 'paletteSelect']
    for (const key of required) {
      if (!this.elements[key]) {
        throw new Error(`TRMNLPicker: Required element "${key}" not found in form`)
      }
    }
  }

  /**
   * Bind event listeners to form elements
   * @private
   */
  _bindEvents() {
    // Store bound handlers for cleanup
    this.handlers = {
      modelChange: this._handleModelChange.bind(this),
      paletteChange: this._handlePaletteChange.bind(this),
      orientationToggle: this._toggleOrientation.bind(this),
      darkModeToggle: this._toggleDarkMode.bind(this),
      fontFamilyChange: this._handleFontFamilyChange.bind(this),
      reset: this._resetToModelDefaults.bind(this)
    }

    // Attach event listeners
    this.elements.modelSelect.addEventListener('change', this.handlers.modelChange)
    this.elements.paletteSelect.addEventListener('change', this.handlers.paletteChange)

    if (this.elements.orientationToggle) {
      this.elements.orientationToggle.addEventListener('click', this.handlers.orientationToggle)
    }

    if (this.elements.darkModeToggle) {
      this.elements.darkModeToggle.addEventListener('click', this.handlers.darkModeToggle)
    }

    if (this.elements.fontFamilySelect) {
      this.elements.fontFamilySelect.addEventListener('change', this.handlers.fontFamilyChange)
    }

    if (this.elements.resetButton) {
      this.elements.resetButton.addEventListener('click', this.handlers.reset)
    }
  }

  /**
   * Set initial state and populate form
   * @private
   */
  _setInitialState() {
    // Separate models into TRMNL and BYOD groups
    const trmnlModels = this.models.filter(m => m.kind === 'trmnl')
    const byodModels = this.models.filter(m => m.kind !== 'trmnl')

    // Sort each group alphabetically by label
    const sortTRMNL = [...trmnlModels].sort((a, b) => {
      const labelA = (a.label || a.name).toLowerCase()
      const labelB = (b.label || b.name).toLowerCase()
      return labelA.localeCompare(labelB)
    })

    const sortBYOD = [...byodModels].sort((a, b) => {
      const labelA = (a.label || a.name).toLowerCase()
      const labelB = (b.label || b.name).toLowerCase()
      return labelA.localeCompare(labelB)
    })

    // Populate model select with grouped models
    this.elements.modelSelect.innerHTML = ''

    // Add TRMNL group
    if (sortTRMNL.length > 0) {
      const trmnlGroup = document.createElement('optgroup')
      trmnlGroup.label = 'TRMNL'
      sortTRMNL.forEach(model => {
        const option = document.createElement('option')
        option.value = model.name
        option.textContent = model.label || model.name
        trmnlGroup.appendChild(option)
      })
      this.elements.modelSelect.appendChild(trmnlGroup)
    }

    // Add BYOD group
    if (sortBYOD.length > 0) {
      const byodGroup = document.createElement('optgroup')
      byodGroup.label = 'BYOD'
      sortBYOD.forEach(model => {
        const option = document.createElement('option')
        option.value = model.name
        option.textContent = model.label || model.name
        byodGroup.appendChild(option)
      })
      this.elements.modelSelect.appendChild(byodGroup)
    }

    // Keep combined sorted list for default selection logic
    const sortedModels = [...sortTRMNL, ...sortBYOD]

    // Populate font-family select if present
    if (this.elements.fontFamilySelect) {
      this.elements.fontFamilySelect.innerHTML = ''
      _FONT_FAMILIES.forEach(font => {
        const option = document.createElement('option')
        option.value = font.value
        option.textContent = font.label
        this.elements.fontFamilySelect.appendChild(option)
      })
    }

    // Initialize state
    this._state = {}

    const defaultModel = sortedModels.find(m => m.name === _DEFAULT_MODEL_NAME) || sortedModels[0]
    const defaultPaletteId = this._getFirstValidPaletteId(defaultModel)
    const defaultParams = {
      modelName: defaultModel.name,
      paletteId: defaultPaletteId,
      isPortrait: false,
      isDarkMode: false,
      fontFamily: _DEFAULT_FONT_FAMILY
    }

    const savedParams = this._loadFromLocalStorage() || {}

    this._setParams('constructor', { ...defaultParams, ...savedParams })
  }

  /**
   * Populate palette dropdown based on selected model
   * @private
   */
  _populateModelPalettes() {
    const modelName = this.elements.modelSelect.value
    const model = this.models.find(m => m.name === modelName)

    if (!model) return

    // Clear existing options
    this.elements.paletteSelect.innerHTML = ''

    // Add options for each palette_id in the model (only if framework_class is not empty)
    model.palette_ids.forEach(paletteId => {
      const palette = this.palettes.find(p => p.id === paletteId)
      if (palette && palette.framework_class && palette.framework_class.trim() !== '') {
        const option = document.createElement('option')
        option.value = palette.id
        option.textContent = palette.name
        this.elements.paletteSelect.appendChild(option)
      }
    })
  }

  /**
   * Emit 'trmnl:change' event with current state and screen classes
   * @private
   * @param {string} origin - Source of the change ('constructor', 'form', 'setParams')
   * @fires TRMNLPicker#trmnl:change
   */
  _emitChangeEvent(origin) {
    // Save to localStorage if key is configured
    this._saveToLocalStorage()

    const event = new CustomEvent('trmnl:change', {
      detail: {
        origin,
        ...this.state,
      },
      bubbles: true
    })

    this.formElement.dispatchEvent(event)
  }

  /**
   * Load state from localStorage
   * @private
   * @returns {Object|null} Saved state or null if not available
   */
  _loadFromLocalStorage() {
    if (!this.localStorageKey) return null

    try {
      const saved = localStorage.getItem(this.localStorageKey)
      if (saved) {
        return JSON.parse(saved)
      }
    } catch (error) {
      console.warn('TRMNLPicker: Failed to load from localStorage:', error)
    }

    return null
  }

  /**
   * Save current state to localStorage
   * @private
   */
  _saveToLocalStorage() {
    if (!this.localStorageKey) return

    try {
      localStorage.setItem(this.localStorageKey, JSON.stringify(this.params))
    } catch (error) {
      console.warn('TRMNLPicker: Failed to save to localStorage:', error)
    }
  }

  /**
   * Handle model selection change
   * @private
   */
  _handleModelChange(event) {
    this._setParams('form', { modelName: event.target.value })
  }

  /**
   * Handle palette selection change
   * @private
   */
  _handlePaletteChange(event) {
    this._setParams('form', { paletteId: event.target.value })
  }

  /**
   * Toggle orientation between portrait and landscape
   * @private
   */
  _toggleOrientation() {
    this._setParams('form', { isPortrait: !this._state.isPortrait })
  }

  /**
   * Toggle dark mode on/off
   * @private
   */
  _toggleDarkMode() {
    this._setParams('form', { isDarkMode: !this._state.isDarkMode })
  }

  /**
   * Handle font-family selection change
   * @private
   */
  _handleFontFamilyChange(event) {
    this._setParams('form', { fontFamily: event.target.value })
  }

  /**
   * Reset to defaults: first valid palette, landscape orientation, light mode, default font-family
   * @private
   */
  _resetToModelDefaults() {
    const model = this._state.model
    if (!model) return

    // Reset to first valid palette of current model
    const firstPaletteId = this._getFirstValidPaletteId(model)

    this._setParams('form', {
      paletteId: firstPaletteId,
      isPortrait: false,
      isDarkMode: false,
      fontFamily: _DEFAULT_FONT_FAMILY
    })
  }

  /**
   * Update reset button enabled/disabled state
   * Button is disabled only when palette, orientation, dark mode, and font-family are all at defaults
   * @private
   */
  _updateResetButton() {
    if (!this.elements.resetButton) return

    const model = this._state.model
    if (!model) return

    const firstValidPaletteId = this._getFirstValidPaletteId(model)
    const isPaletteDefault = this.elements.paletteSelect.value === String(firstValidPaletteId)
    const isOrientationDefault = this._state.isPortrait === false
    const isDarkModeDefault = this._state.isDarkMode === false
    const isFontFamilyDefault = this._state.fontFamily === _DEFAULT_FONT_FAMILY

    const isAtDefaults = isPaletteDefault && isOrientationDefault && isDarkModeDefault && isFontFamilyDefault

    this.elements.resetButton.disabled = isAtDefaults

    if (isAtDefaults) {
      this.elements.resetButton.classList.add('opacity-50', 'cursor-default')
      this.elements.resetButton.setAttribute('aria-disabled', 'true')
    } else {
      this.elements.resetButton.classList.remove('opacity-50', 'cursor-default')
      this.elements.resetButton.removeAttribute('aria-disabled')
    }
  }

  /**
   * Get CSS classes for the current picker configuration
   * @private
   * @returns {Array<string>} Array of CSS class names for Framework CSS rendering
   *
   * Generated classes (in order):
   * 1. 'screen' - Base class (always present)
   * 2. palette.framework_class - From selected palette (e.g., 'screen--1bit')
   * 3. model.css.classes.device - From model API (e.g., 'screen--v2')
   * 4. model.css.classes.size - From model API (e.g., 'screen--md')
   * 5. 'screen--portrait' - Only when portrait orientation is enabled
   * 6. 'screen--1x' - Scale indicator (always 1x)
   * 7. 'screen--dark-mode' - Only when dark mode is enabled
   * 8. 'screen--fonts-{fontFamily}' - Font family (e.g., 'screen--fonts-trmnl')
   *
   * @example
   * const classes = picker.screenClasses
   * // ['screen', 'screen--1bit', 'screen--v2', 'screen--md', 'screen--1x', 'screen--fonts-trmnl']
   */
  get _screenClasses() {
    const model = this._state.model
    const palette = this._state.palette

    if (!model) {
      throw new Error('No model selected')
    }

    const classes = []

    // 0. Base screen class (always present)
    classes.push('screen')

    // 1. Palette framework class
    if (palette && palette.framework_class) {
      classes.push(palette.framework_class)
    }

    // 2. Model device class (from API)
    if (model.css && model.css.classes && model.css.classes.device) {
      classes.push(model.css.classes.device)
    }

    // 3. Model size class (from API)
    if (model.css && model.css.classes && model.css.classes.size) {
      classes.push(model.css.classes.size)
    }

    // 4. Orientation (UI state, only portrait - landscape is default)
    if (this._state.isPortrait) {
      classes.push('screen--portrait')
    }

    // 5. Scale (UI state, always 1x)
    classes.push('screen--1x')

    // 6. Dark mode (UI state, conditional)
    if (this._state.isDarkMode) {
      classes.push('screen--dark-mode')
    }

    // 7. Font family (UI state, always applied)
    if (this._state.fontFamily) {
      classes.push(`screen--fonts-${this._state.fontFamily}`)
    }

    return classes
  }

  /**
   * Get current picker parameters (serializable state)
   * @public
   * @returns {Object} Current parameters for persistence or API calls
   * @returns {string} return.modelName - Selected model name
   * @returns {string} return.paletteId - Selected palette ID
   * @returns {boolean} return.isPortrait - Portrait orientation flag
   * @returns {boolean} return.isDarkMode - Dark mode flag
   * @returns {string} return.fontFamily - Font family ('trmnl' or 'classic')
   *
   * @example
   * const params = picker.params
   * // { modelName: 'og_plus', paletteId: '123', isPortrait: false, isDarkMode: false, fontFamily: 'trmnl' }
   *
   * // Can be used to restore state later
   * localStorage.setItem('picker-state', JSON.stringify(picker.params))
   */
  get params() {
    return {
      modelName: this._state.model?.name,
      paletteId: this._state.palette?.id,
      isPortrait: this._state.isPortrait,
      isDarkMode: this._state.isDarkMode,
      fontFamily: this._state.fontFamily
    }
  }

  /**
   * Update picker configuration programmatically
   * @public
   * @param {Object} params - Configuration object (all fields optional)
   * @param {string} [params.modelName] - Model name to select
   * @param {string} [params.paletteId] - Palette ID to select
   * @param {boolean} [params.isPortrait] - Portrait orientation
   * @param {boolean} [params.isDarkMode] - Dark mode enabled
   * @param {string} [params.fontFamily] - Font family ('trmnl' or 'classic')
   * @fires TRMNLPicker#trmnl:change
   * @throws {Error} If params is not an object
   *
   * @example
   * // Update single parameter
   * picker.setParams({ isDarkMode: true })
   *
   * // Update multiple parameters
   * picker.setParams({
   *   modelName: 'og_plus',
   *   paletteId: '123',
   *   isPortrait: true
   * })
   *
   * // Note: Changing model resets palette to first valid palette of that model
   */
  setParams(params) {
    this._setParams('setParams', params)
  }

  /**
   * Internal method to update picker state with origin tracking
   * @private
   * @param {string} origin - Source of change ('constructor', 'form', 'setParams')
   * @param {Object} params - Parameters to update
   * @returns {boolean} True if any changes were made
   */
  _setParams(origin, params) {
    if (!params || typeof params !== 'object') {
      throw new Error('params must be an object')
    }

    let changed = false

    // Update model if provided
    if (params.modelName) {
      const model = this.models.find(m => m.name === params.modelName)
      if (model) {
        this.elements.modelSelect.value = model.name
        this._state.model = model
        this._populateModelPalettes()

        // Select first valid palette of new model
        const firstPaletteId = this._getFirstValidPaletteId(model)
        this.elements.paletteSelect.value = firstPaletteId
        this._state.palette = this.palettes.find(p => p.id === firstPaletteId)
        changed = true
      }
    }

    // Update palette if provided
    if (params.paletteId) {
      const palette = this.palettes.find(p => p.id === params.paletteId)
      if (palette) {
        this.elements.paletteSelect.value = palette.id
        this._state.palette = palette
        changed = true
      }
    }

    // Update orientation if provided
    if (typeof params.isPortrait === 'boolean') {
      this._state.isPortrait = params.isPortrait
      if (this.elements.orientationText) {
        this.elements.orientationText.textContent = this._state.isPortrait ? 'Portrait' : 'Landscape'
      }
      changed = true
    }

    // Update dark mode if provided
    if (typeof params.isDarkMode === 'boolean') {
      this._state.isDarkMode = params.isDarkMode
      if (this.elements.darkModeText) {
        this.elements.darkModeText.textContent = this._state.isDarkMode ? 'Dark Mode' : 'Light Mode'
      }
      changed = true
    }

    // Update font family if provided
    if (typeof params.fontFamily === 'string') {
      const match = _FONT_FAMILIES.find(f => f.value === params.fontFamily)
      if (match) {
        this._state.fontFamily = match.value
        if (this.elements.fontFamilySelect) {
          this.elements.fontFamilySelect.value = match.value
        }
        changed = true
      }
    }

    if (changed) {
      this._updateResetButton()
    }

    if (changed && origin) {
      this._emitChangeEvent(origin)
    }

    return changed
  }

  /**
   * Get complete picker state including full model and palette objects
   * @public
   * @returns {{
   *   model: Object,
   *   palette: Object,
   *   isPortrait: boolean,
   *   isDarkMode: boolean,
   *   screenClasses: Array<string>,
   *   width: number,
   *   height: number
   * }} State object containing model (full model object from API), palette (full palette object from API), isPortrait flag, and isDarkMode flag
   *
   * @example
   * const state = picker.state
   * // {
   * //   model: { name: 'og_plus', label: 'OG+', width: 800, height: 480, ... },
   * //   palette: { id: '123', name: 'Black', framework_class: 'screen--1bit', ... },
   * //   isPortrait: false,
   * //   isDarkMode: false,
   * //   screenClasses: ['screen', 'screen--1bit', 'screen--v2', 'screen--md', 'screen--1x'],
   * //   width: 800,
   * //   height: 480
   * // }
   */
  get state() {
    return {
      screenClasses: this._screenClasses,
      ...this._dimensions,
      ...this._state,
    };
  }

  /**
   * Get current dimensions (width and height) of the screen in pixels
   * @private
   * @returns {{ width: number, height: number }} Object with width and height properties
   */
  get _dimensions() {
    const model = this._state.model

    let width = model.width / model.scale_factor
    let height = model.height / model.scale_factor

    if (this._state.isPortrait) {
      [width, height] = [height, width]
    }

    return { width, height }
  }

  /**
   * Clean up event listeners and references
   * @public
   */
  destroy() {
    // Remove event listeners
    this.elements.modelSelect.removeEventListener('change', this.handlers.modelChange)
    this.elements.paletteSelect.removeEventListener('change', this.handlers.paletteChange)

    if (this.elements.orientationToggle) {
      this.elements.orientationToggle.removeEventListener('click', this.handlers.orientationToggle)
    }

    if (this.elements.darkModeToggle) {
      this.elements.darkModeToggle.removeEventListener('click', this.handlers.darkModeToggle)
    }

    if (this.elements.fontFamilySelect) {
      this.elements.fontFamilySelect.removeEventListener('change', this.handlers.fontFamilyChange)
    }

    if (this.elements.resetButton) {
      this.elements.resetButton.removeEventListener('click', this.handlers.reset)
    }

    // Clear references
    this.formElement = null
    this.elements = null
    this.handlers = null
    this.models = null
    this.palettes = null
    this._state = null
  }
}

export default TRMNLPicker
